import React, { useEffect, useMemo, useState } from "react";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { format } from "date-fns";
import { Breadcrumb } from "../components/ui/breadcrumb";
import { Button } from "../components/ui/button";
import { Icons } from "../components/ui/icons";
import { useLocation, useNavigate } from "react-router-dom";
import StatisticsCard from "../components/statistics-card";
import {
  ChevronDown,
  ChevronUp,
  Wallet,
  FileText,
  Building,
  Edit2,
} from "lucide-react";
import DataTable, { TableRowData } from "../components/data-table";
import {
  sitesTableColumns,
  quotesTableColumns,
  invoicesTableColumns,
  activitiesTableColumns,
} from "../lib/table-columns";
import { cn, getBackendErrorMessage } from "../lib/utils";
import HorizontalBarGraph from "../components/horizontal-bar-graph";
import BarGraph from "../components/bar-graph";
import QuoteDrawer from "../components/quote-drawer";
import FormDrawer from "../components/form-drawer";
import { RefreshToolbarButton } from "../components/refresh-toolbar-button";
import SelectInput from "../components/select-input";
import ActivityDetailsDrawer from "../components/activity-details-drawer";
import {
  getCustomer,
  getCustomerMovements,
  Customer,
  MovementActivity,
  updateQuotationStatus,
  updateInvoice,
  createSite,
  CreateSitePayload,
} from "../lib/api";
import { siteFormFields } from "../lib/form-fields";
import PageLoadingState from "../components/page-loading-state";
import { Modal } from "../components/ui/modal";
import InvoiceDrawer from "../components/invoice-drawer";
import { toast } from "sonner";
import type { CustomersFiltersState } from "./Customers";
import { useIsMobile } from "../hooks/use-is-mobile";
import MobilePageSkeleton from "../components/mobile/mobile-page-skeleton";
import CustomerDetailsMobile, {
  CustomerNotFoundMobile,
} from "./customer-details/CustomerDetailsMobile";

const customerSiteFormFields = siteFormFields.filter(
  (field) => field.key !== "customer_name",
);

const getMovementSourceName = (movement: MovementActivity) =>
  movement.sourceSite
    ? [movement.sourceSite.customer?.fullName, movement.sourceSite.name]
        .filter(Boolean)
        .join(" - ")
    : movement.sourceWarehouse?.name || movement.sourceVendor?.fullName || "-";

const getMovementDestinationName = (movement: MovementActivity) =>
  movement.destinationSite
    ? [
        movement.destinationSite.customer?.fullName,
        movement.destinationSite.name,
      ]
        .filter(Boolean)
        .join(" - ")
    : movement.destinationWarehouse?.name ||
      movement.destinationCustomer?.fullName ||
      "-";

const CustomerDetails: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const customerId = location.state?.id;
  const queryClient = useQueryClient();

  // Toggle states
  const [showSites, setShowSites] = useState(false);
  const [showMovements, setShowMovements] = useState(false);
  const [showQuotes, setShowQuotes] = useState(false);
  const [showInvoices, setShowInvoices] = useState(false);
  const [selectedQuoteDetails, setSelectedQuoteDetails] =
    useState<TableRowData | null>(null);
  const [selectedInvoiceDetails, setSelectedInvoiceDetails] =
    useState<TableRowData | null>(null);
  const [selectedActivityDetails, setSelectedActivityDetails] =
    useState<TableRowData | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingQuote, setRejectingQuote] = useState<TableRowData | null>(
    null,
  );
  const [isRejecting, setIsRejecting] = useState(false);

  // Approve modal state
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approvingQuote, setApprovingQuote] = useState<TableRowData | null>(
    null,
  );
  const [isApproving, setIsApproving] = useState(false);

  // Mark as paid modal state
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [markingInvoice, setMarkingInvoice] = useState<TableRowData | null>(
    null,
  );
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);

  const [movementsPage, setMovementsPage] = useState(1);
  const [movementsPageSize, setMovementsPageSize] = useState(10);
  const [movementSubTypeFilter, setMovementSubTypeFilter] = useState("all");
  const [movementStatusFilter, setMovementStatusFilter] = useState("all");

  const [siteFormValues, setSiteFormValues] = useState<{
    [key: string]: unknown;
  } | null>(null);
  const [isSubmittingSite, setIsSubmittingSite] = useState(false);

  const handleConfirmReject = async () => {
    if (!rejectingQuote || !customerId) return;
    setIsRejecting(true);
    try {
      await updateQuotationStatus(rejectingQuote.id as string, "REJECTED");
      setShowRejectModal(false);
      setRejectingQuote(null);
      await queryClient.invalidateQueries({
        queryKey: ["customer", customerId],
      });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to reject quotation"));
    } finally {
      setIsRejecting(false);
    }
  };

  const handleConfirmApprove = async () => {
    if (!approvingQuote || !customerId) return;
    setIsApproving(true);
    try {
      await updateQuotationStatus(approvingQuote.id as string, "APPROVED");
      setShowApproveModal(false);
      const q = approvingQuote;
      setApprovingQuote(null);
      // navigate to create invoice with quotation prefilled
      navigate("/create-invoice", { state: { quotationId: q.id } });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to approve quotation"));
    } finally {
      setIsApproving(false);
    }
  };

  const customerQuery = useQuery<Customer>({
    queryKey: ["customer", customerId],
    queryFn: async () => getCustomer(customerId as string),
    enabled: Boolean(customerId),
  });

  const movementSubTypeParam =
    movementSubTypeFilter !== "all" ? movementSubTypeFilter : undefined;
  const movementStatusParam =
    movementStatusFilter !== "all" ? movementStatusFilter : undefined;

  const movementsQuery = useQuery({
    queryKey: [
      "customer",
      customerId,
      "movements",
      {
        page: movementsPage,
        limit: movementsPageSize,
        activitySubType: movementSubTypeParam,
        status: movementStatusParam,
      },
    ],
    queryFn: async () =>
      getCustomerMovements(
        customerId as string,
        movementsPage,
        movementsPageSize,
        movementSubTypeParam,
        movementStatusParam,
      ),
    enabled: Boolean(customerId),
    placeholderData: keepPreviousData,
  });

  const customer = customerQuery.data ?? null;

  useEffect(() => {
    setMovementsPage(1);
  }, [movementSubTypeFilter, movementStatusFilter]);

  const movementsTotalItems = movementsQuery.data?.meta.total ?? 0;
  const movementsTotalPages = movementsQuery.data?.meta.totalPages ?? 1;

  const movements: TableRowData[] = useMemo(() => {
    const data = movementsQuery.data?.data ?? [];
    return data.map((movement: MovementActivity) => ({
      id: movement.id,
      reference: movement.reference,
      date: format(
        new Date(movement.plannedDate || movement.createdAt),
        "MMM dd, yyyy",
      ),
      initiator: movement.initiator?.fullName || "-",
      source: getMovementSourceName(movement),
      destination: getMovementDestinationName(movement),
      activity_type: movement.activityType,
      activity_sub_type: movement.activitySubType,
      project_type: movement.projectType ?? "—",
      status: movement.status,
      raw_data: movement,
    }));
  }, [movementsQuery.data]);

  const fromCustomersFilters = (
    location.state as { fromCustomersFilters?: CustomersFiltersState }
  )?.fromCustomersFilters;
  const breadcrumbItems = [
    {
      label: "Customers",
      href: "/customers",
      state:
        fromCustomersFilters != null
          ? { customersFilters: fromCustomersFilters }
          : undefined,
    },
    { label: "Customer Details" },
  ];

  const goBackToCustomers = () => {
    navigate("/customers", {
      state:
        fromCustomersFilters != null
          ? { customersFilters: fromCustomersFilters }
          : undefined,
    });
  };

  const refreshCustomer = () =>
    queryClient.invalidateQueries({ queryKey: ["customer", customerId] });

  // Map API data to table rows
  const sitesData =
    customer?.sites?.map((s) => ({
      ...s,
      site_name: s.name,
      customer_name: customer.fullName,
      location: `${s.ward}, ${s.district}, ${s.region}`,
      // status matches
    })) || [];

  const quotesData =
    customer?.quotations?.map((q) => ({
      id: q.id || q.reference,
      reference: q.reference,
      customer: customer.fullName,
      site: q.siteId,
      amount: q.totalAmount,
      status: q.status,
    })) || [];

  const invoicesData =
    customer?.invoices?.map((i) => ({
      id: i.id,
      reference: i.reference,
      customer: customer.fullName,
      site: i.siteId,
      issueDate: i.issueDate,
      dueDate: i.dueDate,
      amount: i.totalAmount,
      status: i.status,
    })) || [];

  const siteActions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => {
        navigate("/site-details", {
          state: { id: row.id, site_name: row?.site_name ?? "" },
        });
      },
    },
  ];

  const movementActions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => {
        setSelectedActivityDetails(row);
      },
    },
  ];

  const handleCreateSite = async () => {
    if (!siteFormValues || !customerId) return;

    setIsSubmittingSite(true);

    // Parse location "Ward, District, Region" (same as Sites page)
    const locationStr = (siteFormValues.location as string) || "";
    const locationParts = locationStr.split(",").map((s) => s.trim());
    const region =
      locationParts.length > 0 ? locationParts[locationParts.length - 1] : "";
    const district =
      locationParts.length > 1 ? locationParts[locationParts.length - 2] : "";
    const ward =
      locationParts.length > 2 ? locationParts[locationParts.length - 3] : "";

    const payload: CreateSitePayload = {
      customerId: String(customerId),
      name: siteFormValues.site_name as string,
      street: siteFormValues.site_street as string,
      ward: ward || "",
      district: district || "",
      region: region || "",
      country: "Tanzania",
      status: (siteFormValues.status as "ACTIVE" | "INACTIVE") || "ACTIVE",
    };

    try {
      await createSite(payload);
      toast.success("Site created");
      setSiteFormValues(null);
      setShowSites(true);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["customer", customerId] }),
        queryClient.invalidateQueries({ queryKey: ["sites"] }),
      ]);
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to create site"));
    } finally {
      setIsSubmittingSite(false);
    }
  };

  const quoteAction = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => {
        setSelectedQuoteDetails(row);
      },
    },
    {
      label: "Duplicate",
      icon: Icons.Copy,
      onClick: (row: TableRowData) => {
        navigate("/create-quotation", {
          state: { duplicateQuotationId: row.id },
        });
      },
    },
    {
      label: "Edit",
      icon: Icons.Preview,
      visible: (row: TableRowData) =>
        String(row.status).toUpperCase() === "PENDING",
      onClick: (row: TableRowData) => {
        navigate("/create-quotation", { state: { quotationId: row.id } });
      },
    },
    {
      label: "Approve",
      icon: Icons.Check,
      visible: (row: TableRowData) =>
        String(row.status).toUpperCase() !== "APPROVED" &&
        String(row.status).toUpperCase() !== "REJECTED",
      onClick: (row: TableRowData) => {
        setApprovingQuote(row);
        setShowApproveModal(true);
      },
    },
    {
      label: "Reject",
      icon: Icons.Close,
      visible: (row: TableRowData) =>
        String(row.status).toUpperCase() === "PENDING",
      onClick: (row: TableRowData) => {
        setRejectingQuote(row);
        setShowRejectModal(true);
      },
    },
  ];

  const invoiceActions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => {
        setSelectedInvoiceDetails(row);
      },
    },
    {
      label: "Duplicate",
      icon: Icons.Copy,
      onClick: (row: TableRowData) => {
        navigate("/create-invoice", { state: { duplicateInvoiceId: row.id } });
      },
    },
    {
      label: "Edit",
      icon: Edit2,
      visible: (row: TableRowData) =>
        String(row.status).toUpperCase() !== "PAID",
      onClick: (row: TableRowData) => {
        navigate("/create-invoice", { state: { invoiceId: row.id } });
      },
    },
    {
      label: "Mark as Paid",
      icon: Icons.Check,
      visible: (row: TableRowData) =>
        String(row.status).toUpperCase() !== "PAID",
      onClick: (row: TableRowData) => {
        setMarkingInvoice(row);
        setShowMarkPaidModal(true);
      },
    },
  ];

  const activityGraph = (
    <HorizontalBarGraph
      graphTitle="Activity Summary"
      data={[
        {
          category: "Overview",
          gap: 0.2,
          values: [
            {
              label: "Sites",
              value: customer?.sites?.length || 0,
              fillColor: "#bce3ff",
            },
            {
              label: "Quotes",
              value: customer?.quotations?.length || 0,
              fillColor: "#e9d5ff",
            },
            {
              label: "Invoices",
              value: customer?.invoices?.length || 0,
              fillColor: "#ffd6a8",
            },
          ],
        },
      ]}
    />
  );

  const invoiceStatusGraph = (
    <BarGraph
      graphTitle="Invoice Status Breakdown"
      metrics={[
        {
          label: "Due Soon",
          count: invoicesData.filter((i) => i.status === "DUE SOON").length,
          percentage: 0,
        },
        {
          label: "Paid",
          count: invoicesData.filter((i) => i.status === "PAID").length,
          percentage: 0,
        },
        {
          label: "Overdue",
          count: invoicesData.filter((i) => i.status === "OVERDUE").length,
          percentage: 0,
        },
      ].sort((a, b) => b.count - a.count)}
    />
  );

  if (customerQuery.isLoading)
    return isMobile ? (
      <MobilePageSkeleton />
    ) : (
      <PageLoadingState showAnalytics={true} />
    );

  if (!customer)
    return isMobile ? (
      <CustomerNotFoundMobile onBack={goBackToCustomers} />
    ) : (
      <div className="p-8">Customer not found.</div>
    );

  return (
    <>
      <FormDrawer
        title={customer ? `Add Site for ${customer.fullName}` : "Add Site"}
        buttonText="Add Site"
        formValues={siteFormValues}
        setFormValues={setSiteFormValues}
        fields={customerSiteFormFields}
        handleSubmit={() => void handleCreateSite()}
        isSubmitting={isSubmittingSite}
        onClose={() => setSiteFormValues(null)}
      />
      <QuoteDrawer
        selectedQuoteDetails={selectedQuoteDetails}
        quoteDetails={selectedQuoteDetails}
        onClose={() => {
          setSelectedQuoteDetails(null);
        }}
      />
      <InvoiceDrawer
        selectedInvoiceDetails={selectedInvoiceDetails}
        invoiceDetails={selectedInvoiceDetails}
        onClose={() => {
          setSelectedInvoiceDetails(null);
        }}
      />
      <ActivityDetailsDrawer
        selectedActivityDetails={selectedActivityDetails}
        onClose={() => {
          setSelectedActivityDetails(null);
        }}
        onRefresh={async () => {
          if (!customerId) return;
          try {
            await queryClient.invalidateQueries({
              queryKey: ["customer", customerId, "movements"],
            });
          } catch (error) {
            toast.error(
              getBackendErrorMessage(error, "Failed to refresh movements"),
            );
          }
        }}
      />
      <Modal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Confirm Reject"
        description={
          rejectingQuote
            ? `Are you sure you want to reject quotation ${rejectingQuote.reference || rejectingQuote.id}? This action will mark the quotation as REJECTED.`
            : "Are you sure you want to reject this quotation?"
        }
      >
        <div className="p-6">
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowRejectModal(false)}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 text-white"
              onClick={handleConfirmReject}
              disabled={isRejecting}
            >
              {isRejecting ? "Rejecting..." : "Confirm Reject"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        title="Confirm Approve"
        description={
          approvingQuote
            ? `Approve quotation ${approvingQuote.reference || approvingQuote.id}? This will mark it as APPROVED and open the Create Invoice page prefilled with the quotation.`
            : "Are you sure you want to approve this quotation?"
        }
      >
        <div className="p-6">
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowApproveModal(false)}>
              Cancel
            </Button>
            <Button
              className="bg-primary-600 text-white"
              onClick={handleConfirmApprove}
              disabled={isApproving}
            >
              {isApproving ? "Approving..." : "Confirm Approve"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showMarkPaidModal}
        onClose={() => setShowMarkPaidModal(false)}
        title="Confirm Mark as Paid"
        description={
          markingInvoice
            ? `Are you sure you want to mark invoice ${markingInvoice.reference || markingInvoice.id} as PAID?`
            : "Are you sure?"
        }
      >
        <div className="p-6">
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowMarkPaidModal(false)}>
              Cancel
            </Button>
            <Button
              className="bg-primary-600 text-white"
              onClick={async () => {
                if (!markingInvoice || !customerId) return;
                setIsMarkingPaid(true);
                try {
                  await updateInvoice(markingInvoice.id as string, {
                    status: "PAID",
                  });
                  setShowMarkPaidModal(false);
                  setMarkingInvoice(null);
                  await queryClient.invalidateQueries({
                    queryKey: ["customer", customerId],
                  });
                } catch (error) {
                  toast.error(
                    getBackendErrorMessage(
                      error,
                      "Failed to mark invoice as paid",
                    ),
                  );
                } finally {
                  setIsMarkingPaid(false);
                }
              }}
              disabled={isMarkingPaid}
            >
              {isMarkingPaid ? "Processing..." : "Confirm Mark as Paid"}
            </Button>
          </div>
        </div>
      </Modal>
      {isMobile ? (
        <CustomerDetailsMobile
          customer={customer}
          onBack={goBackToCustomers}
          onRefresh={refreshCustomer}
          onAddSite={() => {
            setShowSites(true);
            setSiteFormValues({ status: "ACTIVE" });
          }}
          canAddSite={Boolean(customerId)}
          insights={
            <>
              {activityGraph}
              {invoiceStatusGraph}
            </>
          }
          sites={{ rows: sitesData, actions: siteActions }}
          movements={{
            rows: movements,
            actions: movementActions,
            totalItems: movementsTotalItems,
            isFetching: movementsQuery.isFetching && !movementsQuery.isLoading,
            pagination: {
              page: movementsPage,
              pageSize: movementsPageSize,
              totalItems: movementsTotalItems,
              totalPages: movementsTotalPages,
              onPageChange: setMovementsPage,
              onPageSizeChange: setMovementsPageSize,
            },
            subTypeFilter: movementSubTypeFilter,
            onSubTypeFilterChange: setMovementSubTypeFilter,
            statusFilter: movementStatusFilter,
            onStatusFilterChange: setMovementStatusFilter,
          }}
          quotes={{ rows: quotesData, actions: quoteAction }}
          invoices={{ rows: invoicesData, actions: invoiceActions }}
        />
      ) : customerQuery.isLoading ? (
        <PageLoadingState showAnalytics={true} />
      ) : (
        <div className="h-full">
          <div className="relative flex flex-1 flex-col bg-white rounded-l-[12px] overflow-hidden border-l border-gray-100">
            <div className="border-b px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="space-y-4">
                  <Breadcrumb items={breadcrumbItems} />
                  <h1 className="text-2xl font-bold font-jakarta">
                    {customer?.fullName}{" "}
                    <span className="text-sm font-normal text-gray-500">
                      ({customer?.type})
                    </span>
                  </h1>
                  <p className="text-sm text-gray-500">
                    {customer?.region}, {customer?.country} |{" "}
                    {customer?.phoneNumber}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <RefreshToolbarButton
                    isRefreshing={customerQuery.isFetching}
                    onRefresh={refreshCustomer}
                  />
                  {/* Reusing SelectInput style for potential actions or switching customers if implemented */}
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-9 w-9 p-0 text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                    onClick={goBackToCustomers}
                  >
                    <Icons.X className="h-4 w-4 cursor-pointer" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatisticsCard
                  label="Total Sites"
                  value={customer?.sites?.length || 0}
                  icon={<Building className="w-5 h-5 text-primary-600" />}
                />
                <StatisticsCard
                  label="Total Quotes"
                  value={customer?.quotations?.length || 0}
                  icon={<FileText className="w-5 h-5 text-blue-600" />}
                />
                <StatisticsCard
                  label="Total Invoices"
                  value={customer?.invoices?.length || 0}
                  icon={<Wallet className="w-5 h-5 text-purple-600" />}
                />
              </div>

              <div className="grid grid-cols-[1fr,400px] gap-4">
                {/* Horizontal Bar: Activity Summary (Sites, Quotes, Invoices) */}
                {activityGraph}

                {/* Bar Graph: Status Breakdown (Example: Invoice Status) */}
                {invoiceStatusGraph}
              </div>

              {/* Sites Section */}
              <div className="border border-gray-200 rounded-2xl transition-all duration-200">
                <div
                  className={cn(
                    "bg-gray-50 px-4 py-3 rounded-t-2xl cursor-pointer",
                    { "rounded-2xl": !showSites },
                  )}
                  onClick={() => setShowSites(!showSites)}
                >
                  <div className="flex items-center justify-between gap-4">
                    <h6 className="font-bold text-lg">
                      Sites
                      {sitesData.length > 0 ? ` (${sitesData.length})` : ""}
                    </h6>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        className="h-8"
                        disabled={!customerId}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowSites(true);
                          setSiteFormValues({ status: "ACTIVE" });
                        }}
                      >
                        <Icons.Plus className="h-4 w-4 mr-1.5" />
                        Add Site
                      </Button>
                      {showSites ? (
                        <ChevronUp className="w-6 h-6" />
                      ) : (
                        <ChevronDown className="w-6 h-6" />
                      )}
                    </div>
                  </div>
                </div>
                {showSites && (
                  <div className="mb-4 bg-white pt-4 px-4 space-y-3">
                    <DataTable
                      columns={sitesTableColumns}
                      data={sitesData}
                      actions={siteActions}
                    />
                  </div>
                )}
              </div>

              {/* Movements Section */}
              <div className="border border-gray-200 rounded-2xl transition-all duration-200">
                <div
                  className={cn(
                    "bg-gray-50 px-4 py-3 rounded-t-2xl cursor-pointer",
                    { "rounded-2xl": !showMovements },
                  )}
                  onClick={() => setShowMovements(!showMovements)}
                >
                  <div className="flex items-center justify-between gap-20">
                    <h6 className="font-bold text-lg">
                      Movements
                      {movementsTotalItems > 0
                        ? ` (${movementsTotalItems})`
                        : ""}
                    </h6>
                    {showMovements ? (
                      <ChevronUp className="w-6 h-6" />
                    ) : (
                      <ChevronDown className="w-6 h-6" />
                    )}
                  </div>
                </div>
                {showMovements && (
                  <div className="mb-4 bg-white pt-4 px-4 space-y-3">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <SelectInput
                        value={movementSubTypeFilter}
                        options={[
                          { label: "All", value: "all" },
                          { label: "Deliver", value: "DELIVER" },
                          { label: "Return", value: "RETURN" },
                          { label: "Transfer", value: "TRANSFER" },
                          { label: "Purchase", value: "PURCHASE" },
                          { label: "Sell", value: "SELL" },
                          { label: "Dispose", value: "DISPOSE" },
                          { label: "Lost", value: "LOST" },
                        ]}
                        placeholder="Subtype"
                        onChange={setMovementSubTypeFilter}
                      />
                      <SelectInput
                        value={movementStatusFilter}
                        options={[
                          { label: "All", value: "all" },
                          { label: "Draft", value: "DRAFT" },
                          { label: "Initiated", value: "INITIATED" },
                          { label: "Approved", value: "APPROVED" },
                          { label: "Closed", value: "CLOSED" },
                          { label: "Cancelled", value: "CANCELLED" },
                        ]}
                        placeholder="Status"
                        onChange={setMovementStatusFilter}
                      />
                    </div>
                    <DataTable
                      columns={activitiesTableColumns}
                      data={movements}
                      actions={movementActions}
                      loading={
                        movementsQuery.isFetching && !movementsQuery.isLoading
                      }
                      pagination={{
                        page: movementsPage,
                        pageSize: movementsPageSize,
                        totalItems: movementsTotalItems,
                        totalPages: movementsTotalPages,
                        onPageChange: setMovementsPage,
                        onPageSizeChange: setMovementsPageSize,
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Quotes Section */}
              <div className="border border-gray-200 rounded-2xl transition-all duration-200">
                <div
                  className={cn(
                    "bg-gray-50 px-4 py-3 rounded-t-2xl cursor-pointer",
                    { "rounded-2xl": !showQuotes },
                  )}
                  onClick={() => setShowQuotes(!showQuotes)}
                >
                  <div className="flex items-center justify-between gap-20">
                    <h6 className="font-bold text-lg">
                      Quotations
                      {quotesData.length > 0 ? ` (${quotesData.length})` : ""}
                    </h6>
                    {showQuotes ? (
                      <ChevronUp className="w-6 h-6" />
                    ) : (
                      <ChevronDown className="w-6 h-6" />
                    )}
                  </div>
                </div>
                {showQuotes && (
                  <div className="mb-4 bg-white pt-4 px-4 space-y-3">
                    <DataTable
                      columns={quotesTableColumns}
                      data={quotesData}
                      actions={quoteAction}
                    />
                  </div>
                )}
              </div>

              {/* Invoices Section */}
              <div className="border border-gray-200 rounded-2xl transition-all duration-200">
                <div
                  className={cn(
                    "bg-gray-50 px-4 py-3 rounded-t-2xl cursor-pointer",
                    { "rounded-2xl": !showInvoices },
                  )}
                  onClick={() => setShowInvoices(!showInvoices)}
                >
                  <div className="flex items-center justify-between gap-20">
                    <h6 className="font-bold text-lg">
                      Invoices
                      {invoicesData.length > 0
                        ? ` (${invoicesData.length})`
                        : ""}
                    </h6>
                    {showInvoices ? (
                      <ChevronUp className="w-6 h-6" />
                    ) : (
                      <ChevronDown className="w-6 h-6" />
                    )}
                  </div>
                </div>
                {showInvoices && (
                  <div className="mb-4 bg-white pt-4 px-4 space-y-3">
                    <DataTable
                      columns={invoicesTableColumns}
                      data={invoicesData}
                      actions={invoiceActions}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerDetails;
